/**
 * Минимальные примитивы окон (C4): оболочка окна, лента-заголовок, кнопки, плашки и иконки. Временно свои —
 * общий набор `ui/kit/` делает C3 (#11); после его слияния эти примитивы переезжают туда (отмечено в MR).
 */
import type { Locale } from "@newfungame/contracts";
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { WindowRoute, WindowViewProps } from "../../app";
import { NUMBER_LOCALES } from "../../i18n";
import { customIcon, Icon as KitIcon, type IconName as KitIconName } from "../kit";
import { pixelIcon, type PixelIconName } from "../kit/pixel-icons";
import "./windows.css";

/** Пропсы подключённого окна: маршрут своего вида и `close()`. */
export type WindowProps<K extends WindowRoute["kind"]> = WindowViewProps<Extract<WindowRoute, { kind: K }>>;

/* ───────────────────────────── оболочка окна ───────────────────────────── */

/** Окна, всплывающие сами посреди боя: тап по навыку в этот момент не должен нажать их кнопку. */
export const TAP_GUARD_MS = 500;

function useTapArmed(guardMs?: number): boolean {
  const [armed, setArmed] = useState(!guardMs);
  useEffect(() => {
    if (armed) return;
    const timer = setTimeout(() => setArmed(true), guardMs);
    return () => clearTimeout(timer);
  }, []);
  return armed;
}

/**
 * Кнопка покупки в окне, где остальное нажимается сразу: случайный тап не должен выставить счёт. Отсчёт идёт с появления
 * кнопки, реванш приходит в ответе сервера и всплывает под пальцем позже окна.
 */
export function PayGuard({ children }: { children: ComponentChildren }) {
  const armed = useTapArmed(TAP_GUARD_MS);
  return (
    <div class="win-pay-guard" data-armed={armed ? "1" : "0"}>
      {children}
    </div>
  );
}

export function WindowShell(props: {
  kind: string;
  tone?: "gold" | "red";
  title: string;
  /** Окно всплывает под пальцем: столько мс кнопки не принимают тап, начатый ради другого. */
  guardMs?: number;
  children: ComponentChildren;
}) {
  const armed = useTapArmed(props.guardMs);
  return (
    <div class={`win win--${props.kind}`} role="dialog" aria-modal="true" data-testid={`window-${props.kind}`}>
      <div class="win__sheet" data-armed={armed ? "1" : "0"}>
        <Ribbon tone={props.tone ?? "gold"}>{props.title}</Ribbon>
        <div class="win__body">{props.children}</div>
      </div>
    </div>
  );
}

export function Ribbon(props: { tone: "gold" | "red"; children: ComponentChildren }) {
  return (
    <h2 class={`win-ribbon win-ribbon--${props.tone}`}>
      <span>{props.children}</span>
    </h2>
  );
}

export function Button(props: {
  tone?: "green" | "blue" | "gray" | "link";
  children: ComponentChildren;
  onClick(): void;
  disabled?: boolean;
  "data-testid"?: string;
}) {
  const { tone = "green", children, onClick, disabled } = props;
  return (
    <button
      type="button"
      class={`win-btn win-btn--${tone}`}
      disabled={disabled}
      onClick={onClick}
      data-testid={props["data-testid"]}
    >
      {children}
    </button>
  );
}

export function Panel(props: { class?: string; children: ComponentChildren; testid?: string }) {
  return (
    <div class={`win-panel ${props.class ?? ""}`} data-testid={props.testid}>
      {props.children}
    </div>
  );
}

/* ───────────────────────────── иконки ───────────────────────────── */

export type IconName =
  | "coin"
  | "bolt"
  | "sword"
  | "target"
  | "flame"
  | "shield"
  | "wrench"
  | "cannon"
  | "clock"
  | "snow"
  | "arrows"
  | "bounce"
  | "range"
  | "star"
  | "crystal"
  | "chest"
  | "check"
  | "share"
  | "play"
  | "finger"
  | "bug"
  | "bulb"
  | "chat";

const PATHS: Record<IconName, JSX.Element> = {
  coin: (
    <>
      <circle cx="12" cy="12" r="10" fill="#f2b630" stroke="#a8700f" stroke-width="2" />
      <rect x="9" y="7" width="6" height="10" rx="1" fill="#ffd866" />
    </>
  ),
  bug: (
    <>
      <path
        d="M12 8v13M6 11 2.5 9M18 11l3.5-2M6 15H2M18 15h4M6.5 18 3 21M17.5 18l3.5 3"
        stroke="#1b1f2a"
        stroke-width="1.6"
        fill="none"
      />
      <ellipse cx="12" cy="14" rx="6" ry="7" fill="#e0402c" stroke="#5c0c10" stroke-width="1.5" />
      <path d="M12 7v14" stroke="#5c0c10" stroke-width="1.5" />
      <circle cx="12" cy="6" r="3" fill="#1b1f2a" />
    </>
  ),
  bulb: (
    <>
      <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" fill="#ffd93b" stroke="#a8700f" stroke-width="1.5" />
      <rect x="9" y="18" width="6" height="3" rx="1" fill="#7d8696" />
    </>
  ),
  chat: (
    <path
      d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H10l-4 4v-4H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"
      fill="#8fe6f8"
      stroke="#2c6e99"
      stroke-width="1.5"
    />
  ),
  bolt: <path d="M13 2 4 14h6l-1 8 9-12h-6z" fill="#ffd23f" stroke="#a8700f" stroke-width="1.5" />,
  sword: (
    <>
      <path d="M19 3 9 13l2 2L21 5V3z" fill="#dfe6ee" stroke="#5b6b7a" stroke-width="1.5" />
      <path d="M6 14l4 4-2 2-4-4zM4 18l2 2-2 2-2-2z" fill="#b5651d" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="9" fill="#e84a4a" />
      <circle cx="12" cy="12" r="6" fill="#fff" />
      <circle cx="12" cy="12" r="3" fill="#e84a4a" />
    </>
  ),
  flame: (
    <path
      d="M12 2c1 4 6 6 6 12a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-4-1-6 1-9z"
      fill="#ff7a1a"
      stroke="#b33a00"
      stroke-width="1.5"
    />
  ),
  shield: <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z" fill="#4f8fe6" stroke="#244b8a" stroke-width="1.5" />,
  wrench: (
    <path
      d="M21 7a5 5 0 0 1-7 4.6L6 19.6 4.4 18 12.4 10A5 5 0 0 1 17 3l-3 3 1 3 3 1z"
      fill="#c9d3dd"
      stroke="#5b6b7a"
      stroke-width="1.5"
    />
  ),
  cannon: (
    <>
      <rect x="3" y="8" width="15" height="7" rx="3" fill="#3c4450" transform="rotate(-20 10 12)" />
      <circle cx="8" cy="17" r="4" fill="#7a4a1d" />
      <circle cx="20" cy="6" r="2" fill="#3c4450" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" fill="#8fd3ff" stroke="#2c6e99" stroke-width="2" />
      <path d="M12 6v6l4 3" stroke="#12324a" stroke-width="2" fill="none" stroke-linecap="round" />
    </>
  ),
  snow: (
    <path
      d="M12 2v20M3.3 7l17.4 10M3.3 17 20.7 7M9 4l3 2 3-2M9 20l3-2 3 2"
      stroke="#bfefff"
      stroke-width="2.4"
      fill="none"
      stroke-linecap="round"
    />
  ),
  arrows: (
    <path
      d="M3 6h12l-3-3M3 12h15l-3-3M3 18h12l-3-3"
      stroke="#ffd23f"
      stroke-width="2.4"
      fill="none"
      stroke-linecap="round"
    />
  ),
  bounce: <path d="M3 18 9 6l6 12 6-12" stroke="#7df0a8" stroke-width="2.4" fill="none" stroke-linejoin="round" />,
  range: (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="#7df0a8" stroke-width="2" stroke-dasharray="3 3" />
      <circle cx="12" cy="12" r="3" fill="#7df0a8" />
    </>
  ),
  star: (
    <path
      d="m12 2 3 7 7 .6-5.3 4.7 1.6 7.2L12 17.8 5.7 21.5l1.6-7.2L2 9.6 9 9z"
      fill="#ffd23f"
      stroke="#a8700f"
      stroke-width="1.5"
    />
  ),
  crystal: (
    <path
      d="M7 3h10l5 6-10 13L2 9z M2 9h20 M9 9l3 13 3-13 M7 3l2 6 3-6 3 6 2-6"
      fill="#6fd8f0"
      stroke="#1e6f94"
      stroke-width="1.5"
      stroke-linejoin="round"
    />
  ),
  chest: (
    <>
      <rect x="3" y="9" width="18" height="11" rx="1" fill="#8a5a2b" stroke="#4d2f12" stroke-width="1.5" />
      <path d="M3 9a9 5 0 0 1 18 0z" fill="#a8703a" stroke="#4d2f12" stroke-width="1.5" />
      <rect x="10.5" y="11" width="3" height="4" fill="#f2b630" />
    </>
  ),
  check: <path d="m4 12 5 5L20 6" stroke="#5fe08a" stroke-width="3.5" fill="none" stroke-linecap="round" />,
  share: <path d="M22 3 2 11l7 3 2 7 3-5 5 4z" fill="#fff" />,
  play: <path d="M7 4v16l13-8z" fill="#fff" />,
  finger: (
    <path
      d="M10 2a2 2 0 0 1 2 2v7l5 1a3 3 0 0 1 2 3v3a5 5 0 0 1-5 5h-2a5 5 0 0 1-4-2l-3-4a1.6 1.6 0 0 1 2.4-2L8 15V4a2 2 0 0 1 2-2z"
      fill="#fff"
      stroke="#1b1f2a"
      stroke-width="1.6"
    />
  ),
};

export function Icon(props: { name: IconName; size?: number; class?: string }) {
  const s = props.size ?? 24;
  // чиби-атлас, а без него монета — воксельная иконка A5 (kit/pixel-icons)
  if (props.name === "coin" || customIcon(props.name, s)) {
    return <KitIcon name={props.name as KitIconName} size={s} class={props.class} />;
  }
  return (
    <svg
      class={`win-icon ${props.class ?? ""}`}
      width={s}
      height={s}
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-icon={props.name}
    >
      {PATHS[props.name]}
    </svg>
  );
}

/** Иконка усиления по id из данных баланса; новое усиление без своей иконки — звезда. */
const BOOST_ICONS: Record<string, IconName> = {
  boost_damage_up: "sword",
  boost_fire_rate: "bolt",
  boost_long_range: "range",
  boost_crit_chance: "target",
  boost_cannon_power: "cannon",
  boost_hq_armor: "shield",
  boost_coin_rush: "coin",
  boost_quick_skills: "clock",
  boost_ignite: "flame",
  boost_crit_damage: "target",
  boost_ricochet: "bounce",
  boost_cannon_rapid: "cannon",
  boost_hq_regen: "wrench",
  boost_multishot: "arrows",
  boost_skill_power: "bolt",
  boost_frost_aura: "snow",
  boost_overdrive: "flame",
  boost_endless_damage: "sword",
  boost_endless_armor: "shield",
  boost_endless_coins: "coin",
};

/** Усиление: воксельная иконка A5 по id; для нового id без неё — прежняя запасная. */
export function BoostIcon(props: { boostId: string; size?: number; class?: string }) {
  if (pixelIcon(props.boostId)) {
    return <KitIcon name={props.boostId as PixelIconName} size={props.size ?? 44} class={props.class} />;
  }
  return <Icon name={boostIcon(props.boostId)} size={props.size} class={props.class} />;
}

export function boostIcon(boostId: string): IconName {
  return BOOST_ICONS[boostId] ?? "star";
}

/** Портрет героя-заглушка: круг цвета героя с первой буквой (спрайты героев — A2). */
const HERO_COLORS: Record<string, string> = {
  gunner: "#3f7fd8",
  bomber: "#d8573f",
  sparky: "#e5c32e",
  blaze: "#ef7d22",
  frosty: "#5ec8e8",
};

export function HeroBadge(props: { heroId: string; name: string }) {
  if (customIcon(props.heroId, 40)) return <KitIcon name={props.heroId as KitIconName} size={40} />;
  return (
    <span class="win-hero" style={{ background: HERO_COLORS[props.heroId] ?? "#888" }} aria-hidden="true">
      {props.name.slice(0, 1)}
    </span>
  );
}

/** Монеты: до 100 000 — полностью с разрядами, дальше коротко: 2 100 000 → «2.1M» / «2,1 млн». */
export function compactCoins(n: number, locale: string): string {
  if (n < 100_000) return n.toLocaleString(NUMBER_LOCALES[locale as Locale]);
  return new Intl.NumberFormat(NUMBER_LOCALES[locale as Locale], {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}
